import React, { useCallback, useState } from "react";
import { Route, useHistory, useParams, useRouteMatch } from "react-router-dom";
import { useDexDocumentDownloadUrlLazyQuery } from "#graphql/supergraphHooks";
import { useDocumentDetail } from "../../hooks/useDocumentDetail";
import { useExtractedFields } from "../../hooks/useExtractedFields";
import { usePipelineStatuses } from "../../hooks/usePipelineStatuses";
import { useUploadDocument } from "../../hooks/useUploadDocument";
import { DocumentDetail } from "../DocumentDetail";
import { IncomingUploads } from "../IncomingUploads";
import * as styles from "./DexApp.css";

const joinPath = (base: string, id: string) => `${base}/${id}`;

const DocumentDetailRoute: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { documentId } = useParams<{ documentId: string }>();
  const history = useHistory();
  const { status, loading, error } = useDocumentDetail(documentId);
  const { fields, loading: fieldsLoading, error: fieldsError } = useExtractedFields(documentId);
  const [getDownloadUrl, { loading: downloadLoading }] = useDexDocumentDownloadUrlLazyQuery();
  const [downloadError, setDownloadError] = useState(false);

  // Enabled once the row exists — the original PDF is stored at upload, not a later
  // stage. A null URL means the row has no stored key (legacy/spike rows) → inline error.
  const handleDownload = async () => {
    setDownloadError(false);
    try {
      const res = await getDownloadUrl({ variables: { documentId } });
      const url = res.data?.dexDocumentDownloadUrl;
      if (url) window.open(url, "_blank", "noopener");
      else setDownloadError(true);
    } catch {
      setDownloadError(true);
    }
  };

  return (
    <DocumentDetail
      status={status}
      loading={loading}
      error={Boolean(error)}
      fields={fields}
      fieldsLoading={fieldsLoading}
      fieldsError={Boolean(fieldsError)}
      onClose={() => history.push(basePath)}
      onDownload={() => void handleDownload()}
      downloadDisabled={!status}
      downloadLoading={downloadLoading}
      downloadError={downloadError}
    />
  );
};

/**
 * Self-contained root of the Dex experience — the host renders `<DexApp />`
 * with no props. Assumes it is already authorized (no `CAN_ACCESS_DEX` re-check)
 * and that the host provides the ambient federation `ApolloProvider`, a `@jsq/ds`
 * `<ToastContainer />`, and a react-router v5 `Router`. See the README.
 */
export const DexApp: React.FC = () => {
  // path = the route pattern (may carry :params) for <Route>; url = the concrete
  // matched path (params resolved) for navigation. Never hardcode /dex.
  const { path, url } = useRouteMatch();
  const routeBase = path.replace(/\/$/, "");
  const navBase = url.replace(/\/$/, "");
  const history = useHistory();
  const { uploadDocument } = useUploadDocument();
  const { statuses, loading, error, refetch, page, totalPages, setPage } =
    usePipelineStatuses();

  // Show the just-uploaded document in the table without a manual refresh.
  // The refetch is best-effort: a transient refresh failure must not surface as
  // an upload failure (the table's own error state already covers a bad refresh).
  const handleUpload = useCallback(
    async (file: File) => {
      await uploadDocument(file);
      setPage(1);
      void refetch().catch(() => {});
    },
    [uploadDocument, setPage, refetch],
  );

  return (
    <div className={styles.layout}>
      <IncomingUploads
        statuses={statuses}
        loading={loading}
        error={Boolean(error)}
        onUpload={handleUpload}
        onRefresh={() => void refetch().catch(() => {})}
        onRowSelect={(id) => history.push(joinPath(navBase, id))}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
      {/* exact: an arbitrary multi-segment deep link (e.g. a stale bookmark) must not
          be swallowed as a documentId — only a true single-segment detail path opens it. */}
      <Route exact path={`${routeBase}/:documentId`}>
        <DocumentDetailRoute basePath={navBase} />
      </Route>
    </div>
  );
};
